import { inject, Injectable } from "@angular/core";
import { AuthStore } from "../+state/auth";
import { FormioForm } from "@formio/angular";
import { FormComponent } from "../models/form-component.model";

@Injectable({
  providedIn: "root",
})
export class SubmissionFormService {
  readonly #authStore = inject(AuthStore);

  createForm(formData: { components: object[] | null }) {
    if (formData?.components) {
      formData.components = formData.components.map(
        (c: {
          buttonSettings?: { cancel?: boolean; next: boolean; previous: boolean; submit: boolean };
          title?: string;
          subTitle?: string;
        }) => {
          if (c.buttonSettings) {
            c.buttonSettings.cancel = false;
            c.buttonSettings.submit = !this.#authStore.isEditorRole();
          }

          if (c.title?.includes("|")) {
            const titleParts = c.title.split("|");
            c.title = titleParts[titleParts.length - 1]?.trim();
          } else {
            const match = c.title?.match(/^([A-Za-z]+\s+\d+(?:\.\d+)+)(?:\s+(.+))?$/);
            if (match && match.length > 1) {
              c.title = match[1];
              c["subTitle"] = match[2];
            } else {
              c["subTitle"] = "";
            }
          }
          return c;
        },
      );
    }

    return formData as FormioForm;
  }

  /**
   * Collects all component keys from a Form.io component tree.
   * For components with selectable values (like permission dropdowns),
   * also collects the valid option values.
   */
  collectFormKeys(components: FormComponent[], keySet: Set<string>, validValues: Map<string, Set<string>>): void {
    if (!components) return;
    for (const component of components) {
      if (component.key) {
        keySet.add(component.key);
        if (component.data?.values) {
          validValues.set(component.key, new Set(component.data.values.map((v) => v.value)));
        }
      }
      // Standard nested containers (panel, fieldset, datagrid, editgrid, etc.) store children here.
      if (component.components) {
        this.collectFormKeys(component.components, keySet, validValues);
      }
      // `table` layout components store their children under rows[][].components, not `.components`.
      if (Array.isArray(component.rows)) {
        for (const row of component.rows) {
          if (!Array.isArray(row)) continue;
          for (const cell of row) {
            if (cell?.components) {
              this.collectFormKeys(cell.components, keySet, validValues);
            }
          }
        }
      }
      // `columns` layout components store their children under columns[].components.
      if (Array.isArray(component.columns)) {
        for (const column of component.columns) {
          if (column?.components) {
            this.collectFormKeys(column.components, keySet, validValues);
          }
        }
      }
    }
  }

  /**
   * Filters submission data to only include keys that exist in the target
   * form schema, and nullifies values that are not among the target form's
   * valid options for that field.
   */
  sanitizeSubmissionData(
    sourceData: Record<string, unknown> | null | undefined,
    targetComponents: FormComponent[] | undefined,
  ): Record<string, unknown> {
    if (!sourceData) return {};
    if (!targetComponents) return sourceData;

    const targetKeys = new Set<string>();
    const targetValidValues = new Map<string, Set<string>>();
    this.collectFormKeys(targetComponents, targetKeys, targetValidValues);

    const filtered: Record<string, unknown> = {};
    for (const [key, value] of Object.entries(sourceData)) {
      if (!targetKeys.has(key)) continue;

      const allowedValues = targetValidValues.get(key);
      if (allowedValues && typeof value === "string" && !allowedValues.has(value)) {
        filtered[key] = null;
      } else {
        filtered[key] = value;
      }
    }

    return filtered;
  }
}
